import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiLoader, FiKey, FiCheck, FiUser, FiMapPin } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { joinGroupByInviteCode, getGroupDetailsByInviteCode } from '../../services/groupService';
import posthog from 'posthog-js';
import { trackJoinGroup } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import PaywallModal from '../PaywallModal';

/**
 * Reusable form for joining a group by 8-character invite code. Used by:
 *  - /group/join (full page)
 *  - the guided onboarding wizard
 *
 * Two-step flow: enter code → preview group → confirm to join.
 * On successful join, calls `onSuccess(group)`.
 */
function JoinByCodeForm({ onSuccess, onCancel, submitLabel = 'Preview Group' }) {
  const { user } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState('');
  const [groupPreview, setGroupPreview] = useState(null);
  const [paywallFeature, setPaywallFeature] = useState(null);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError('');
    try {
      const { data, error: previewError } = await getGroupDetailsByInviteCode(inviteCode.trim());
      if (previewError) {
        setError(previewError.message || 'Invalid invite code');
        setLoadingPreview(false);
        return;
      }
      setGroupPreview(data);
    } catch (err) {
      setError('Failed to load group details');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleJoin = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: joinError } = await joinGroupByInviteCode(inviteCode.trim());
      if (joinError) {
        if (joinError.code === 'PLAN_CAP_GROUPS') {
          setPaywallFeature('groups');
        } else if (joinError.code === 'PLAN_CAP_GROUP_SIZE') {
          setPaywallFeature('group_size');
        } else {
          setError(joinError.message || 'Failed to join group');
        }
        setLoading(false);
        return;
      }
      if (user?.id && data?.group?.id) {
        trackJoinGroup(user.id, data.group.id);
        posthog.group('group', data.group.id, {
          name: data.group.name,
        });
      }
      onSuccess?.(data.group);
    } catch (err) {
      setError('Failed to join group. Please try again.');
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }
    await fetchPreview();
  };

  if (groupPreview) {
    return (
      <div className="space-y-4">
        <div className="bg-surface border border-border-subtle rounded-md overflow-hidden">
          <div className="divide-y divide-border-subtle">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center">
                <div className="w-9 h-9 bg-accent/10 rounded-md flex items-center justify-center mr-3">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 text-accent" />
                </div>
                <span className="text-sm text-text-secondary">Group</span>
              </div>
              <span className="text-sm font-semibold text-text-primary text-right truncate max-w-[180px]">
                {groupPreview.groupName}
              </span>
            </div>
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center">
                <div className="w-9 h-9 bg-accent/10 rounded-md flex items-center justify-center mr-3">
                  <SafeIcon icon={FiUsers} className="w-4 h-4 text-accent" />
                </div>
                <span className="text-sm text-text-secondary">Members</span>
              </div>
              <span className="text-sm font-semibold text-text-primary">{groupPreview.memberCount}</span>
            </div>
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center">
                <div className="w-9 h-9 bg-accent/10 rounded-md flex items-center justify-center mr-3">
                  <SafeIcon icon={FiUser} className="w-4 h-4 text-accent" />
                </div>
                <span className="text-sm text-text-secondary">Created by</span>
              </div>
              <span className="text-sm font-semibold text-text-primary">{groupPreview.ownerName}</span>
            </div>
            {groupPreview.homeCourse && (
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center">
                  <div className="w-9 h-9 bg-accent/10 rounded-md flex items-center justify-center mr-3">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 text-accent" />
                  </div>
                  <span className="text-sm text-text-secondary">Home course</span>
                </div>
                <span className="text-sm font-semibold text-text-primary text-right max-w-[180px] truncate">
                  {groupPreview.homeCourse}
                </span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm"
          >
            {error}
          </motion.div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => {
              setGroupPreview(null);
              setInviteCode('');
            }}
            disabled={loading}
            className="flex-1 px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors font-medium text-sm"
          >
            Use different code
          </button>
          {groupPreview.isAlreadyMember ? (
            <button
              onClick={() => onSuccess?.({ id: groupPreview.groupId, name: groupPreview.groupName, invite_code: inviteCode, alreadyMember: true })}
              className="flex-1 px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover font-medium text-sm flex items-center justify-center"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
              Already a member
            </button>
          ) : (
            <button
              onClick={handleJoin}
              disabled={loading}
              className="flex-1 px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 font-semibold text-sm flex items-center justify-center"
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                  Join group
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleManualSubmit} className="space-y-4">
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-md text-sm"
        >
          {error}
        </motion.div>
      )}

      <div>
        <label htmlFor="inviteCode" className="block text-sm font-medium text-text-secondary mb-2">
          Invite code
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted">
            <SafeIcon icon={FiKey} className="w-4 h-4" />
          </div>
          <input
            type="text"
            id="inviteCode"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            className="w-full pl-10 pr-4 py-3 border border-border-subtle rounded-md focus:ring-1 focus:ring-accent focus:border-accent font-mono text-base tracking-widest text-center font-medium bg-surface"
            placeholder="ABCD1234"
            maxLength={8}
            autoFocus
            required
          />
        </div>
        <p className="text-xs text-text-muted mt-2">
          Ask the group creator to share their 8-character code.
        </p>
      </div>

      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loadingPreview}
            className="flex-1 px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors font-medium text-sm"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loadingPreview}
          className="flex-1 flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 font-semibold text-sm"
        >
          {loadingPreview ? (
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
          ) : (
            submitLabel
          )}
        </button>
      </div>
      <PaywallModal
        isOpen={!!paywallFeature}
        onClose={() => setPaywallFeature(null)}
        feature={paywallFeature}
      />
    </form>
  );
}

export default JoinByCodeForm;
